// Plane entity manager - handles creation, animation, and lifecycle of planes with contrails
import * as THREE from 'three';

export class PlaneManager {
    constructor(scene, camera, worldTranslator, config = {}, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.worldTranslator = worldTranslator;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('planes', {
            dispose: () => this.dispose()
        });

        // Configuration
        this.enabled = config.enabled !== undefined ? config.enabled : true;
        this.size = config.size || 15;
        this.minHeight = config.minHeight || 600;
        this.maxHeight = config.maxHeight || 3000;
        this.minSpawnDistance = config.minSpawnDistance || 3000;
        this.maxSpawnDistance = config.maxSpawnDistance || 4000;
        this.speed = config.speed || 200;
        this.minSpawnInterval = config.minSpawnInterval || 30; // seconds
        this.maxSpawnInterval = config.maxSpawnInterval || 60; // seconds
        this.maxCount = config.maxCount || 2;

        // Contrail settings
        this.contrailSegmentLength = config.contrailSegmentLength || 30; // Shorter segments for smoother trails
        this.contrailMaxSegments = config.contrailMaxSegments || 200; // More segments to accommodate shorter length
        this.contrailFadeTime = config.contrailFadeTime || 25; // Longer fade for persistent trails

        // Active planes
        this.planes = [];
        this.nextPlaneSpawnTime = 0;

        // Contrail object pool (Phase 3.1 optimization)
        this._contrailPool = [];
        this._contrailPoolMaxSize = 500; // Cap pool size to prevent unbounded growth
        this._tempVec1 = new THREE.Vector3();
        this._tempVec2 = new THREE.Vector3();

        // Cached navigation light textures (shared across all planes)
        this._redLightTexture = null;
        this._greenLightTexture = null;
        this._initNavigationLightTextures();

        // Disposal state
        this._disposed = false;
    }

    /**
     * Initialize cached navigation light textures (called once)
     */
    _initNavigationLightTextures() {
        // Red light texture
        const redCanvas = document.createElement('canvas');
        redCanvas.width = 64;
        redCanvas.height = 64;
        const redCtx = redCanvas.getContext('2d');
        const redGradient = redCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
        redGradient.addColorStop(0, 'rgba(255, 100, 100, 1)');
        redGradient.addColorStop(0.3, 'rgba(255, 0, 0, 1)');
        redGradient.addColorStop(1, 'rgba(255, 0, 0, 0)');
        redCtx.fillStyle = redGradient;
        redCtx.fillRect(0, 0, 64, 64);
        this._redLightTexture = new THREE.CanvasTexture(redCanvas);

        // Green light texture
        const greenCanvas = document.createElement('canvas');
        greenCanvas.width = 64;
        greenCanvas.height = 64;
        const greenCtx = greenCanvas.getContext('2d');
        const greenGradient = greenCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
        greenGradient.addColorStop(0, 'rgba(100, 255, 100, 1)');
        greenGradient.addColorStop(0.3, 'rgba(0, 255, 0, 1)');
        greenGradient.addColorStop(1, 'rgba(0, 255, 0, 0)');
        greenCtx.fillStyle = greenGradient;
        greenCtx.fillRect(0, 0, 64, 64);
        this._greenLightTexture = new THREE.CanvasTexture(greenCanvas);
    }

    // Get a contrail line from pool or create new
    _acquireContrail(startPos, endPos, opacity, creationTime) {
        let line;

        if (this._contrailPool.length > 0) {
            // Reuse from pool
            line = this._contrailPool.pop();
            const positions = line.geometry.attributes.position.array;
            positions[0] = startPos.x;
            positions[1] = startPos.y;
            positions[2] = startPos.z;
            positions[3] = endPos.x;
            positions[4] = endPos.y;
            positions[5] = endPos.z;
            line.geometry.attributes.position.needsUpdate = true;
            line.geometry.computeBoundingSphere(); // Recompute for frustum culling
            line.material.opacity = opacity;
            line.visible = true;
        } else {
            // Create new
            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(6); // 2 points * 3 components
            positions[0] = startPos.x;
            positions[1] = startPos.y;
            positions[2] = startPos.z;
            positions[3] = endPos.x;
            positions[4] = endPos.y;
            positions[5] = endPos.z;
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

            const material = new THREE.LineBasicMaterial({
                color: 0xffffff,
                transparent: true,
                opacity: opacity,
                fog: true
            });
            line = new THREE.Line(geometry, material);
        }

        line.userData.creationTime = creationTime;
        line.userData.endPosition = this._tempVec1.copy(endPos).clone(); // Need actual clone for storage
        line.userData.baseOpacity = opacity;

        return line;
    }

    // Return contrail line to pool (with cap to prevent unbounded growth)
    _releaseContrail(line) {
        line.visible = false;
        this.worldTranslator.remove(line);

        // Cap pool size to prevent memory growth
        if (this._contrailPool.length < this._contrailPoolMaxSize) {
            this._contrailPool.push(line);
        } else {
            // Pool is full - dispose instead of pooling
            line.geometry.dispose();
            line.material.dispose();
        }
    }

    createPlane(xPosition, direction) {
        // Create plane container (parent object)
        const plane = new THREE.Group();

        // Simple plane body (fuselage)
        const bodyGeometry = new THREE.BoxGeometry(this.size * 0.3, this.size * 0.2, this.size);
        const bodyMaterial = new THREE.MeshLambertMaterial({ color: 0xcccccc, fog: true });
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
        plane.add(body);

        // Wings
        const wingGeometry = new THREE.BoxGeometry(this.size * 3, this.size * 0.1, this.size * 0.5);
        const wingMaterial = new THREE.MeshLambertMaterial({ color: 0xcccccc, fog: true });
        const wings = new THREE.Mesh(wingGeometry, wingMaterial);
        wings.position.y = -this.size * 0.05;
        plane.add(wings);

        // Tail
        const tailGeometry = new THREE.BoxGeometry(this.size * 0.8, this.size * 0.5, this.size * 0.1);
        const tailMaterial = new THREE.MeshLambertMaterial({ color: 0xcccccc, fog: true });
        const tail = new THREE.Mesh(tailGeometry, tailMaterial);
        tail.position.z = this.size * 0.45;
        tail.position.y = this.size * 0.15;
        plane.add(tail);

        // Red sprite (left wing) - using cached texture
        const redSpriteMaterial = new THREE.SpriteMaterial({
            map: this._redLightTexture,
            transparent: true,
            blending: THREE.AdditiveBlending,
            fog: true
        });
        const redSprite = new THREE.Sprite(redSpriteMaterial);
        redSprite.scale.set(this.size * 0.6, this.size * 0.6, 1);
        redSprite.position.set(-this.size * 1.5, 0, 0);
        redSprite.userData.isNavLight = true; // Mark as nav light (shared texture)
        plane.add(redSprite);

        // Green sprite (right wing) - using cached texture
        const greenSpriteMaterial = new THREE.SpriteMaterial({
            map: this._greenLightTexture,
            transparent: true,
            blending: THREE.AdditiveBlending,
            fog: true
        });
        const greenSprite = new THREE.Sprite(greenSpriteMaterial);
        greenSprite.scale.set(this.size * 0.6, this.size * 0.6, 1);
        greenSprite.position.set(this.size * 1.5, 0, 0);
        greenSprite.userData.isNavLight = true; // Mark as nav light (shared texture)
        plane.add(greenSprite);

        // Position plane far ahead of camera (in local worldGroup coordinates)
        // Use worldTranslator's spawn position (negative Z = ahead)
        const y = this.minHeight + Math.random() * (this.maxHeight - this.minHeight);
        const spawnDistance = this.minSpawnDistance + Math.random() * (this.maxSpawnDistance - this.minSpawnDistance);
        const z = -spawnDistance; // Planes spawn ahead in local coordinates
        plane.position.set(xPosition, y, z);

        // Orient plane to face direction of travel and tilt for better visibility
        if (direction === 1) {
            // Flying right - rotate nose to point right
            plane.rotation.y = -Math.PI / 2;
        } else {
            // Flying left - rotate nose to point left
            plane.rotation.y = Math.PI / 2;
        }
        // Tilt plane slightly downward so we can see the top/wings better
        plane.rotation.x = -0.1;
        plane.rotation.z = 0.05 * direction;

        // Store movement data
        plane.userData.direction = direction; // 1 = right, -1 = left
        plane.userData.speed = this.speed;
        plane.userData.contrail = []; // Array of contrail segments
        plane.userData.distanceTraveled = 0;
        plane.userData.zOffset = (Math.random() - 0.5) * 500;
        plane.userData.redSprite = redSprite;
        plane.userData.greenSprite = greenSprite;

        this.worldTranslator.add(plane);
        return plane;
    }

    initialize() {
        if (!this.enabled) {
            this._onPreloadComplete?.();
            return;
        }

        // Initialize spawn timer
        this.nextPlaneSpawnTime = this.minSpawnInterval +
            Math.random() * (this.maxSpawnInterval - this.minSpawnInterval);

        this._onPreloadComplete?.();
    }

    spawnPlane() {
        if (this.planes.length >= this.maxCount) return;

        // Spawn from left or right edge, far away in the fog
        const direction = Math.random() < 0.5 ? 1 : -1; // 1 = fly right, -1 = fly left
        const spawnDistanceX = 5000 + Math.random() * 1000;
        const xPosition = direction === 1 ? -spawnDistanceX : spawnDistanceX;
        const plane = this.createPlane(xPosition, direction);
        this.planes.push(plane);

    }

    update(deltaTime, ambientLightIntensity) {
        if (this._disposed || !this.enabled) return;

        const currentTime = performance.now() / 1000;

        // Handle plane spawning at intervals
        this.nextPlaneSpawnTime -= deltaTime;
        if (this.nextPlaneSpawnTime <= 0) {
            this.spawnPlane();
            // Set next spawn time
            this.nextPlaneSpawnTime = this.minSpawnInterval +
                Math.random() * (this.maxSpawnInterval - this.minSpawnInterval);
        }

        // Update existing planes
        for (let i = this.planes.length - 1; i >= 0; i--) {
            const plane = this.planes[i];

            // Move plane horizontally
            const movement = plane.userData.speed * deltaTime * plane.userData.direction;
            plane.position.x += movement;
            plane.userData.distanceTraveled += Math.abs(movement);

            // Show navigation lights only at night
            const showLights = ambientLightIntensity < 0.3;
            plane.userData.redSprite.visible = showLights;
            plane.userData.greenSprite.visible = showLights;

            // Create contrail segments
            const isDaytime = ambientLightIntensity >= 0.3;
            const contrailOpacity = isDaytime ? 0.8 : 0.1;

            if (plane.userData.distanceTraveled >= this.contrailSegmentLength) {
                plane.userData.distanceTraveled = 0;

                // Use temp vector instead of clone
                const currentPos = this._tempVec2.copy(plane.position);

                if (plane.userData.contrail.length > 0) {
                    // Create line from last segment to current position using pool
                    const lastSegment = plane.userData.contrail[plane.userData.contrail.length - 1];
                    const line = this._acquireContrail(
                        lastSegment.userData.endPosition,
                        currentPos,
                        contrailOpacity,
                        currentTime
                    );
                    this.worldTranslator.add(line);
                    plane.userData.contrail.push(line);
                } else {
                    // First segment - store position for next line
                    const dummySegment = {
                        userData: {
                            creationTime: currentTime,
                            endPosition: currentPos.clone() // Need clone here for storage
                        }
                    };
                    plane.userData.contrail.push(dummySegment);
                }

                // Limit contrail length - return to pool instead of dispose
                if (plane.userData.contrail.length > this.contrailMaxSegments) {
                    const oldSegment = plane.userData.contrail.shift();
                    if (oldSegment.geometry) {
                        this._releaseContrail(oldSegment);
                    }
                }
            }

            // Fade contrail segments
            for (let j = plane.userData.contrail.length - 1; j >= 0; j--) {
                const segment = plane.userData.contrail[j];

                // Skip dummy segments
                if (!segment.geometry) continue;

                const age = currentTime - segment.userData.creationTime;
                const fadeProgress = age / this.contrailFadeTime;

                if (fadeProgress >= 1.0) {
                    // Remove fully faded segment - return to pool
                    plane.userData.contrail.splice(j, 1);
                    this._releaseContrail(segment);
                } else {
                    // Fade opacity
                    const baseOpacity = segment.userData.baseOpacity || 0.8;
                    segment.material.opacity = baseOpacity * (1 - fadeProgress);
                }
            }

            // Remove plane if off screen or behind camera
            // In world translation model, positive local Z = behind camera
            const isOffScreenX = Math.abs(plane.position.x) > 8000;
            const isBehindCamera = this.worldTranslator.isPastCamera(plane.position.z, 1000);

            if (isOffScreenX || isBehindCamera) {
                // Remove plane and contrail
                this.worldTranslator.remove(plane);
                plane.children.forEach(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        // Don't dispose shared navigation light textures
                        if (child.material.map && !child.userData.isNavLight) {
                            child.material.map.dispose();
                        }
                        child.material.dispose();
                    }
                });

                // Return all contrail segments to pool
                plane.userData.contrail.forEach(segment => {
                    if (segment.geometry) {
                        this._releaseContrail(segment);
                    }
                });

                // Remove from array
                this.planes.splice(i, 1);
            }
        }
    }

    dispose() {
        if (this._disposed) return;
        this._disposed = true;

        // Clean up all planes
        this.planes.forEach(plane => {
            this.worldTranslator.remove(plane);
            plane.children.forEach(child => {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    // Don't dispose shared navigation light textures here
                    if (child.material.map && !child.userData.isNavLight) {
                        child.material.map.dispose();
                    }
                    child.material.dispose();
                }
            });

            // Clean up contrails
            plane.userData.contrail.forEach(segment => {
                if (segment.geometry) {
                    this.worldTranslator.remove(segment);
                    segment.geometry.dispose();
                    segment.material.dispose();
                }
            });
        });
        this.planes = [];

        // Clean up contrail pool
        this._contrailPool.forEach(line => {
            line.geometry.dispose();
            line.material.dispose();
        });
        this._contrailPool = [];

        // Dispose shared navigation light textures
        if (this._redLightTexture) {
            this._redLightTexture.dispose();
            this._redLightTexture = null;
        }
        if (this._greenLightTexture) {
            this._greenLightTexture.dispose();
            this._greenLightTexture = null;
        }
    }
}
